import { faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { Bundle, BundleEntry, Device, DocumentReference, FhirResource, Patient, Practitioner, RelatedPerson } from "fhir/r4";
import { Dispatch, FunctionComponent, SetStateAction, useEffect, useReducer, useRef, useState } from "react";
import { Button, Dropdown, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DeviceForm from "../DeviceForm/DeviceForm";
import PatientForm from "../PatientForm/PatientForm";
import PractitionerForm from "../PractitionerForm/PractitionerForm";
import RelatedPersonForm from "../RelatedPersonForm/RelatedPersonForm";
import styles from "./NoteForm.module.css";

interface Author {
    id: string,
    type: 'Practitioner' | 'RelatedPerson' | 'Organization' | 'Device',
    create: boolean,
    value: FhirResource | BundleEntry<FhirResource>[] | undefined
};

const NoteForm: FunctionComponent<{
    submitTrigger?: number,
    resetTrigger?: number,
    displayButtons?: boolean,
    getPatientChoices?: (setter: Dispatch<SetStateAction<Patient[]>>) => void;
    getPractitionerChoices?: (setter: Dispatch<SetStateAction<Practitioner[]>>) => void;
    getRelatedPersonChoices?: (setter: Dispatch<SetStateAction<RelatedPerson[]>>) => void;
    getDeviceChoices?: (setter: Dispatch<SetStateAction<Device[]>>) => void;
    noteSubmit: (bundle: Bundle) => void
}> = (props) => {
    const { t } = useTranslation();
    const choiceLoadersRef = useRef({
        getPatientChoices: props.getPatientChoices,
        getPractitionerChoices: props.getPractitionerChoices,
        getRelatedPersonChoices: props.getRelatedPersonChoices,
        getDeviceChoices: props.getDeviceChoices,
    });
    choiceLoadersRef.current = {
        getPatientChoices: props.getPatientChoices,
        getPractitionerChoices: props.getPractitionerChoices,
        getRelatedPersonChoices: props.getRelatedPersonChoices,
        getDeviceChoices: props.getDeviceChoices,
    };
    const submitNoteRef = useRef<() => void>(() => undefined);
    submitNoteRef.current = createNoteBundleAndSubmitIt;

    useEffect(() => {
        const choiceLoaders = choiceLoadersRef.current;

        if (choiceLoaders.getPatientChoices) {
            choiceLoaders.getPatientChoices(setPatientChoices);
        }
        if (choiceLoaders.getPractitionerChoices) {
            choiceLoaders.getPractitionerChoices(setPractitionerChoices);
        }
        if (choiceLoaders.getRelatedPersonChoices) {
            choiceLoaders.getRelatedPersonChoices(setRelatedPersonChoices);
        }
        if (choiceLoaders.getDeviceChoices) {
            choiceLoaders.getDeviceChoices(setDeviceChoices);
        }
    }, []);

    const [form, setForm] = useState({
        status: 'current',
        type: 'DEM-AVIS',
        content: '',
    });

    const [validated, setValidated] = useState(false);

    async function onSubmit(event: any) {
        event.preventDefault();
        const formtv = event.currentTarget;
        if (formtv.checkValidity() === true) {
            triggerSubmit();
            setTrySubmit(true);
        }
        setValidated(true);
    }

    const onReset = () => {
        setForm({
            status: 'current',
            type: 'DEM-AVIS',
            content: '',
        });
        setAuthors([]);
        triggerReset();
        setValidated(false);
    }

    const [trySubmit, setTrySubmit] = useState(false);

    const [createPatient, setCreatePatient] = useState(false);
    const [patientChoices, setPatientChoices] = useState([] as Patient[]);
    const [patient, setPatient] = useState(undefined as Patient | undefined)

    const [counter, forceUpdate] = useReducer(x => x + 1, 0);
    const [authors, setAuthors] = useState([] as Author[]);

    const [practitionerChoices, setPractitionerChoices] = useState([] as Practitioner[]);
    const [relatedPersonChoices, setRelatedPersonChoices] = useState([] as RelatedPerson[]);
    const [deviceChoices, setDeviceChoices] = useState([] as Device[]);

    const [resetTrigger, triggerReset] = useReducer(x => x + 1, 0);
    const [submitTrigger, triggerSubmit] = useReducer(x => x + 1, 0);

    useEffect(() => {
        if (patient !== undefined && authors.length > 0 && !authors.find(a => a.value === undefined) && trySubmit === true) {
            submitNoteRef.current();
        }
    }, [authors, counter, patient, trySubmit]);

    const handleChange = (event: { target: { name: any; value: any; }; }) => {
        setForm({ ...form, [event.target.name]: event.target.value });
    }

    function createNoteBundleAndSubmitIt() {
        const transactionBundle = { "resourceType": "Bundle" } as Bundle;
        transactionBundle.type = "transaction";
        transactionBundle.entry = [];

        var patientUuid: string;
        if (createPatient) {
            patientUuid = "urn:uuid:7353f046-dcce-4787-8a3e-d652ab0827aa"
            transactionBundle.entry.push({
                "fullUrl": patientUuid,
                "resource": patient,
                "request": {
                    "method": "POST",
                    "url": "/Patient"
                }
            } as BundleEntry);
        } else {
            patientUuid = "Patient/" + patient?.id;
        }

        const note = {
            "resourceType": "DocumentReference",
            "id": "1",
            "status": form.status,
            "type": {
                "coding": [
                    {
                        "system": "https://mos.esante.gouv.fr/NOS/TRE_R234-TypeNote/FHIR/TRE-R234-TypeNote",
                        "code": form.type,
                        "display": getTypeDisplay()
                    }
                ]
            },
            "subject": {
                "reference": patientUuid
            },
            "author": [
            ],
            "content": [
                {
                    "attachment": {
                        "data": form.content,
                        "contentType": "image/png"
                    }
                }
            ]
        } as DocumentReference;
        note.date = dayjs(new Date()).format("YYYY-MM-DDTHH:mm:ssZ");

        authors.forEach(a => {
            if (a.create) {
                switch (a.type) {
                    case "Practitioner":
                        (a.value as BundleEntry<FhirResource>[]).forEach(e => transactionBundle.entry?.push(e));
                        break;
                    case "RelatedPerson":
                        const relatedPersonUuid = "urn:uuid:fa865478-ad92-4d12-88d5-3409205dba3b";
                        (a.value as RelatedPerson).patient = { "reference": patientUuid };
                        transactionBundle.entry?.push({
                            "fullUrl": relatedPersonUuid,
                            "resource": a.value,
                            "request": {
                                "method": "POST",
                                "url": "/RelatedPerson"
                            }
                        } as BundleEntry);
                        break;
                    case "Organization":
                        break;
                    case "Device":
                        const deviceUuid = "urn:uuid:41b7a285-95b0-422a-9ab3-ad039ce3e4ed"
                        transactionBundle.entry?.push({
                            "fullUrl": deviceUuid,
                            "resource": a.value,
                            "request": {
                                "method": "POST",
                                "url": "/Device"
                            }
                        } as BundleEntry);
                        break;
                }
            } else {
                note.author?.push({ "reference": a.type + "/" + (a.value as FhirResource).id })
            }
        })

        transactionBundle.entry.forEach(entry => { note.author?.push({ "reference": entry.fullUrl }) });

        const noteUuid = "urn:uuid:e478aedd-0cb1-4b50-8957-5201a3f891fa"
        transactionBundle.entry.push({
            "fullUrl": noteUuid,
            "resource": note,
            "request": {
                "method": "POST",
                "url": "/DocumentReference"
            }
        } as BundleEntry);

        props.noteSubmit(transactionBundle);
    }

    function getTypeDisplay() {
        return t(`forms.note.typeOptions.${form.type}`, { defaultValue: form.type });
    }

    function getAuthorTypeLabel(type: Author["type"]) {
        return t(`forms.note.authorTypes.${type}`);
    }

    function areChoicesDefined(author: Author): boolean {
        switch (author.type) {
            case 'Practitioner':
                return props.getPractitionerChoices !== undefined;
            case "RelatedPerson":
                return props.getRelatedPersonChoices !== undefined;
            case "Organization":
                return false;
            case "Device":
                return props.getDeviceChoices !== undefined;
        }
    }

    function getAuthorForm(author: Author) {
        switch (author.type) {
            case 'Practitioner':
                return <PractitionerForm
                    submitTrigger={submitTrigger}
                    resetTrigger={resetTrigger}
                    dataSubmit={(pro) => {
                        author.value = pro;
                        setAuthors(authors);
                        forceUpdate();
                    }} />;
            case "RelatedPerson":
                return <RelatedPersonForm
                    submitTrigger={submitTrigger}
                    resetTrigger={resetTrigger}
                    relatedPersonSubmit={(rp) => {
                        author.value = rp;
                        forceUpdate()
                    }} />;
            case "Organization":
                return <></>
            case "Device":
                return <DeviceForm
                    submitTrigger={submitTrigger}
                    resetTrigger={resetTrigger}
                    deviceSubmit={(device) => {
                        author.value = device;
                        forceUpdate();
                    }} />;
        }
    }

    function getAuthorChoices(author: Author) {
        switch (author.type) {
            case 'Practitioner':
                return <Form.Select
                    className="select"
                    defaultValue={undefined}
                    onChange={(event) => {
                        author.value = practitionerChoices.find(practitioner => event.target.value === practitioner.id);
                    }}
                >
                    <option
                        value={undefined}
                    >
                        {t("forms.note.selectAuthor", { type: getAuthorTypeLabel("Practitioner") })}
                    </option>
                    {practitionerChoices.map((practitioner) => {
                        return <option
                            value={practitioner.id}
                        >
                            {practitioner.name?.map(name => name.given?.join(",") + " " + name.family)}
                        </option>
                    })}
                </Form.Select>;
            case "RelatedPerson":
                return <Form.Select
                    className="select"
                    defaultValue={undefined}
                    onChange={(event) => {
                        author.value = relatedPersonChoices.find(relatedPerson => event.target.value === relatedPerson.id);
                    }}
                >
                    <option
                        value={undefined}
                    >
                        {t("forms.note.selectAuthor", { type: getAuthorTypeLabel("RelatedPerson") })}
                    </option>
                    {relatedPersonChoices.map((relatedPerson) => {
                        return <option
                            value={relatedPerson.id}
                        >
                            {relatedPerson.name?.map(name => name.given?.join(",") + " " + name.family)}
                        </option>
                    })}
                </Form.Select>;
            case "Organization":
                return <></>;
            case "Device":
                return <Form.Select
                    className="select"
                    defaultValue={undefined}
                    onChange={(event) => {
                        author.value = deviceChoices.find(device => event.target.value === device.id);
                    }}
                >
                    <option
                        value={undefined}
                    >
                        {t("forms.note.selectAuthor", { type: getAuthorTypeLabel("Device") })}
                    </option>
                    {deviceChoices.map((device) => {
                        return <option
                            value={device.id}
                        >
                            {device.deviceName?.map(name => name.name)}
                        </option>
                    })}
                </Form.Select>;
        }
    }

    return <Form className='container mt-3 mb-3' noValidate validated={validated} onSubmit={onSubmit}>

        <Form.Group className="col">
            <Form.Label>{t("forms.note.status")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="status"
                value={form.status}
                onChange={handleChange}
            >
                {(["current", "superseded", "entered-in-error"] as const).map((status) => <option key={status} value={status}>{t(`forms.note.statusOptions.${status}`)}</option>)}
            </Form.Select>
        </Form.Group>
        <Form.Group className="col">
            <Form.Label>{t("forms.note.type")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="type"
                value={form.type}
                onChange={handleChange}
            >
                {(["DEM-AVIS", "GEN", "INST", "INTERV", "OBS"] as const).map((type) => <option key={type} value={type}>{t(`forms.note.typeOptions.${type}`)}</option>)}
            </Form.Select>
        </Form.Group>
        <Form.Group className="col">
            <Form.Label>{t("forms.note.subject")} :</Form.Label>
            <Form.Check
                type="switch"
                label={t("forms.note.createPatient")}
                checked={createPatient}
                onChange={() => {
                    setCreatePatient(!createPatient);
                }
                }
                isInvalid={!createPatient && !props.getPatientChoices}
            />

            {createPatient ? (
                <Form.Group className={styles.subForm}>
                    <PatientForm submitTrigger={submitTrigger} resetTrigger={resetTrigger} patientSubmit={setPatient} />
                </Form.Group>
            ) : (props.getPatientChoices ?
                <>
                    <Form.Select
                        className="select"
                        defaultValue={undefined}
                        onChange={(event) => {
                            //TODO => take the patient from ID
                            setPatient(patientChoices.find(patient => event.target.value === patient.id));
                        }}
                    >
                        <option
                            value={undefined}
                        >
                            {t("forms.note.selectPatient")}
                        </option>
                        {patientChoices.map((patient) => {
                            return <option
                                value={patient.id}
                            >
                                {patient.name?.map(name => name.given?.join(",") + " " + name.family)}
                            </option>
                        })}
                    </Form.Select>
                </>
                : <div className="invalid-feedback" style={{ display: "flex" }}>{t("forms.note.existingUnavailable", { type: t("label.patient") })}</div>
            )}

        </Form.Group>
        <br />
        <Dropdown>
            <Dropdown.Toggle variant="primary">
                {t("forms.note.addAuthor")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item
                    onClick={() => {
                        authors.push({ id: String(counter), type: 'Practitioner', create: false, value: undefined });
                        forceUpdate();
                    }
                    }
                >
                    {getAuthorTypeLabel("Practitioner")}
                </Dropdown.Item>

                <Dropdown.Item
                    onClick={() => {
                        authors.push({ id: String(counter), type: 'RelatedPerson', create: false, value: undefined });
                        forceUpdate();
                    }
                    }
                >
                    {getAuthorTypeLabel("RelatedPerson")}
                </Dropdown.Item>

                <Dropdown.Item
                    onClick={() => {
                        authors.push({ id: String(counter), type: 'Device', create: false, value: undefined });
                        forceUpdate();
                    }
                    }
                >
                    {getAuthorTypeLabel("Device")}
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
        <br />

        {authors.map(author => {
            return <Form.Group className="col">
                <Form.Label>
                    {t("forms.note.author", { type: getAuthorTypeLabel(author.type) })}:&nbsp;&nbsp;&nbsp;
                    <FontAwesomeIcon icon={faXmarkCircle} onClick={() => {
                        setAuthors(authors.filter(a => a.id !== author.id));
                        forceUpdate();
                    }} />
                </Form.Label>
                <Form.Check
                    type="switch"
                    label={t("forms.note.createAuthor", { type: getAuthorTypeLabel(author.type) })}
                    checked={author.create}
                    onChange={() => {
                        author.create = !author.create;
                        author.value = undefined;
                        forceUpdate();
                    }
                    }
                />

                {author.create ? (
                    <>
                        <Form.Group className={styles.subForm}>
                            {getAuthorForm(author)}
                        </Form.Group>
                    </>
                ) : (areChoicesDefined(author) ?
                    <>
                        {getAuthorChoices(author)}
                    </>
                    : <div className="invalid-feedback" style={{ display: "flex" }}>{t("forms.note.existingUnavailable", { type: getAuthorTypeLabel(author.type) })}</div>
                )}
            </Form.Group>
        })}

        <Form.Group className="col">
            <Form.Label>{t("forms.note.content")}</Form.Label>
            <Form.Control
                type="text"
                name="content"
                value={form.content}
                onChange={handleChange}
                className="form-control"
                required />
            <Form.Control.Feedback type="invalid">
                {t("forms.note.invalidContent")}
            </Form.Control.Feedback>
        </Form.Group>

        <Row className="mb-3">
            <Form.Group className="col">
                <Button
                    type='submit'
                    className="button"
                    variant="danger"
                >
                    {t("forms.common.submit")}
                </Button>
                <Button
                    className="button"
                    variant="secondary"
                    onClick={onReset}
                >
                    {t("forms.common.reset")}
                </Button>
            </Form.Group>
        </Row>
    </Form>;
};

export default NoteForm;
